import React, { useState, useEffect, useCallback } from 'react';
import GlassCard from '../../../shared/components/card/GlassCard';
import DotGrid from './DotGrid';
import { format, subDays, addDays, isSameDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../pages/Layout';

/**
 * Componente genérico que muestra una sección de datos (equipos, empleados u organismos).
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.sectionName - Título de la sección.
 * @param {Function} props.renderCard - Función que renderiza cada item.
 * @param {'worker'|'team'|'organismo'} props.cardType - Tipo de datos a mostrar.
 */
const TeamsSection = ({ sectionName, renderCard, cardType }) => {
    const { getEquipos, getEmpleados, getOrganismos } = useAuth();
    const { currentDate, setIsLoading } = useDate();
    
    // Estado unificado
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const date = format(currentDate, 'yyyy-MM-dd')

    // Configuración de tipos de datos
    const DATA_CONFIG = {
        worker: {
            fetchFn: () => {return getEmpleados(date)},
            emptyMessage: 'No hay empleados disponibles',
            keyExtractor: (item) => item.idEmpleado || item.id || item.nombre
        },
        team: {
            fetchFn: () => {return getEquipos(date)},
            emptyMessage: 'No hay equipos disponibles',
            keyExtractor: (item) => item.idEquipo || item.id || item.nombre
        },
        organismo: {
            fetchFn: () => { return getOrganismos(date)},
            emptyMessage: 'No hay organismos disponibles',
            keyExtractor: (item) => item.idOrganismo || item.id || item.codigo
        }
    };

    const fetchData = useCallback(async () => {
        const config = DATA_CONFIG[cardType];
        
        if (!config) {
            setError(`Tipo de card no soportado: ${cardType}`);
            setLoading(false);
            return;
        }

        setLoading(true);
        setIsLoading(true);
        setError(null);

        try {
            const response = await config.fetchFn();
            console.log("Lista : ",response)
            const fetchedData = response?.data || [];
            setData(fetchedData);
        } catch (err) {
            console.error(`Error cargando ${cardType}:`, err);
            setError(`No se pudieron cargar los datos de ${sectionName}`);
            setData([]);
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    }, [cardType, currentDate, setIsLoading]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Render de estados
    if (loading) {
        return (
            <section className="teams-section teams-section--loading">
                <GlassCard maxWidth="100%">
                    <div className="stats-content frosted-pulse">
                        <div className="loading-overlay">
                            <DotGrid />
                        </div>
                        <div className="blurred-content">
                            {/* Contenido placeholder que pulsa */}
                            <div className="main-stat">
                                <span className="stat-label">Cargando...</span>
                                <div className="stat-value">
                                    <span className="amount">--.--</span>
                                    <span className="currency">€</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </GlassCard>
            </section>
        );
    }

    if (error) {
        return (
            <section className="teams-section teams-section--error">
                <GlassCard maxWidth="100%">
                    <div className="teams-section__error">
                        <span className="error-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </span>
                        <p>{error}</p>
                        <button 
                            className="retry-btn"
                            onClick={fetchData}
                        >
                            Reintentar
                        </button>
                    </div>
                </GlassCard>
            </section>
        );
    }

    const config = DATA_CONFIG[cardType];
    const isEmpty = data.length === 0;

    return (
        <section className="teams-section">
            <GlassCard maxWidth="100%">
                <div className="teams-section__header">
                    <h2 className="teams-section__title">{sectionName}</h2>
                    <span className="teams-section__count">
                        {data.length} {data.length === 1 ? 'elemento' : 'elementos'}
                    </span>
                </div>
                
                {isEmpty ? (
                    <div className="teams-section__empty">
                        <p>{config?.emptyMessage || 'No hay datos disponibles'}</p>
                    </div>
                ) : (
                    <div className="teams-grid">
                        {data.map((item, index) => (
                            <React.Fragment key={config?.keyExtractor(item) || index}>
                                {renderCard(item)}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </GlassCard>
        </section>
    );
};

export default TeamsSection;